import { useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, DataTable, Modal, type TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/types/user';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { SettingsUnreadable } from './Unreadable';
import { useClinicDomains, type ClinicDomains } from './useSettings';

/**
 * Clinic manager · Settings ▸ Domains — `g_set_dom.dc.html`.
 *
 * TWO ROWS, BECAUSE A CLINIC HAS TWO ADDRESSES: the one its staff sign in at
 * and the one printed in every patient message. The board's own accent card
 * says which of the two a clinic actually changes.
 *
 * READ IS THE MANAGER'S, PROVISIONING IS THE OWNER'S. `/tenants/:id/hosts` is
 * gated on tenant ownership on every verb including the GET, deliberately —
 * registering a host changes where the clinic LIVES. So the table reads
 * `GET /clinic-settings/domains`, a projection with `hospital_settings:read`
 * on it, and the two buttons the board draws are marked as the owner's rather
 * than opening a form that would be refused at the end of it (§4.8: the mark
 * goes on the control, not in the aftermath).
 *
 * THE ROWS ARE THE TENANT'S REAL HOSTS, not a rendering of the slug. A clinic
 * with no patient-portal host of its own sits on the shared path host, which
 * is a different row with a different tier, and the screen says so.
 */

/** `clear-cairo.app.maie.to` — a subdomain row's URL. */
function emrUrl(d: ClinicDomains): string | null {
  const host = d.hosts.find((h) => h.surface === 'emr' && h.status === 'active');
  if (host?.customDomain) return host.customDomain;
  if (host?.subdomainSlug) return `${host.subdomainSlug}.${d.publicHostBase}`;
  if (d.slug) return `${d.slug}.${d.publicHostBase}`;
  return null;
}

/** `care.maie.to/clear-cairo` — the portal's address, host-bound or shared. */
function portalUrl(d: ClinicDomains): string | null {
  const host = d.hosts.find((h) => h.surface === 'patient' && h.status === 'active');
  if (host?.customDomain) return host.customDomain;
  if (host?.subdomainSlug) return `${host.subdomainSlug}.${d.patientHostBase}`;
  if (d.slug) return `${d.patientPortalGenericHost}/${d.slug}`;
  return null;
}

export function ManagerDomainsPage() {
  const go = useNavigate();
  const { value, isLoading, error, refetch } = useClinicDomains();
  const [query, setQuery] = useState('');
  const [asking, setAsking] = useState<'subdomain' | 'custom' | null>(null);
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const isOwner = roleCheck.hasRole(UserRole.OWNER);

  usePageMeta({ title: 'Domains · System Settings' });

  const reading = isLoading && !value;
  const unreadable = !reading && !value;

  const rows = value ? [
    {
      url: emrUrl(value),
      sub: 'where the clinic signs in',
      tier: value.hosts.some((h) => h.surface === 'emr' && h.customDomain)
        ? 'Custom domain' : 'Managed subdomain',
      active: !!emrUrl(value),
      cert: value.hosts.some((h) => h.surface === 'emr' && h.customDomain) ? 'custom' : 'auto',
    },
    {
      url: portalUrl(value),
      sub: 'every link in a patient thread carries it',
      tier: 'Patient portal',
      active: !!portalUrl(value),
      cert: value.hosts.some((h) => h.surface === 'patient' && h.customDomain) ? 'custom' : 'auto',
    },
  ] : [];

  const q = query.trim().toLowerCase();
  const shown = rows.filter((r) => !q || `${r.url ?? ''} ${r.sub} ${r.tier}`.toLowerCase().includes(q));

  const crumb = usePhoneCrumb('Settings', 'Domains');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Domains',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Domains
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="domains" />,
      /* The board's two buttons: the outline one rides `extra` (the slot
         between the bell and the primary), the filled one IS the primary. */
      extra: unreadable ? undefined : (
        <button type="button" className="btn btn-outline" onClick={() => setAsking('subdomain')}>
          Enable subdomain
        </button>
      ),
      primary: unreadable ? undefined : {
        label: '+ Add custom domain',
        onClick: () => setAsking('custom'),
      },
    },
    [query, unreadable, isOwner],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s addresses…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="addresses" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {value ? (
          <>
            <DataTable
              caption="Addresses"
              columns={[
                { key: 'url', label: 'URL', width: 46 },
                { key: 'tier', label: 'Tier', width: 20 },
                { key: 'status', label: 'Status', width: 17, role: 'trailing' },
                { key: 'cert', label: 'Cert', width: 17 },
              ]}
              rows={shown.map((r): TableRow => ({
                id: r.sub,
                cells: {
                  url: (
                    <div>
                      <p className="t-strong">
                        {r.url ?? <span className="mut">Not provisioned</span>}
                      </p>
                      <p className="t-cap mut">{r.sub}</p>
                    </div>
                  ),
                  tier: <span className="mut">{r.tier}</span>,
                  status: r.active
                    ? <Chip kind="success" small>Active</Chip>
                    : <Chip kind="outline" small>Not set up</Chip>,
                  /* A managed address is certificated by the platform and
                     renews itself; a custom domain's certificate is issued
                     only after its DNS verifies, so it is not "Auto ✓" until
                     it is. */
                  cert: r.active && r.cert === 'auto'
                    ? <span className="w6 grn">Auto ✓</span>
                    : <span className="mut">—</span>,
                },
              }))}
            />

            {!shown.length ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No address on this screen matches that. Clear the search to see both again.
              </p>
            ) : null}

            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Certificates renew themselves. Custom domains are a paid-plan feature and the DNS
              steps are shown after the domain is added — never before, when they would only be
              noise.
            </p>

            <Card kind="accent">
              <p className="t-sec brand">
                The portal address is the one a clinic actually changes, because it is printed on
                every message a patient receives.
              </p>
            </Card>
          </>
        ) : null}
      </div>

      <Modal
        open={asking !== null}
        onClose={() => setAsking(null)}
        title={asking === 'custom' ? 'Add custom domain' : 'Enable subdomain'}
        sub="who may change where this clinic lives"
        width={460}
        foot={<button type="button" className="btn btn-outline" onClick={() => setAsking(null)}>Close</button>}
      >
        <div style={{ marginTop: 12 }}>
          <p className="t-sec sec">
            {isOwner
              ? 'This is yours to do, from the owner’s Domains screen: it registers the host and returns the TXT and CNAME records to add at your registrar.'
              : 'Registering an address is the tenant owner’s act, not a settings permission: it changes where staff sign in and what every patient link points at. Ask the owner of this clinic to add it.'}
          </p>
          {asking === 'custom' ? (
            <p className="t-cap mut mt8">
              {value?.customDomainsEnabled
                ? 'This deployment allows custom domains. The DNS steps appear once the domain is added.'
                : 'This deployment does not allow custom domains at all.'}
            </p>
          ) : (
            <p className="t-cap mut mt8">
              {value?.patientSubdomainsEnabled
                ? 'A managed patient subdomain can be provisioned for this clinic.'
                : 'Managed patient subdomains are switched off on this deployment, so the portal stays on the shared address above.'}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
